import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { auth, db } from "../firebase";
import { useNotifications } from "../hooks/notificationsContext";
import useDisplayNames from "../utils/useDisplayNames";
import { doc, getDoc } from "firebase/firestore";

function TransferSharesModal({ company, userData, onClose, refreshUserData }) {
  const [shares, setShares] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taxes, setTaxes] = useState(0);
  const [recipient, setRecipient] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const { addNotification } = useNotifications();
  const { players: allRecipients } = useDisplayNames();

  const recipientRef = useRef(null);
  const suggestionsRef = useRef(null);

  const priceUSD = company?.companyStockPrice || 0;

  const userHoldings = useMemo(() => {
    if (!company || !userData) return 0;
    if (company.companyTicker === "XAU") return userData.userGold ?? 0;
    return userData[`company${company.companyTicker}`] ?? 0;
  }, [company, userData]);

  const maxShares = Math.floor(userHoldings);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  useEffect(() => {
    const calculateTaxes = async () => {
      if (!company || shares < 1 || !userData) {
        setTaxes(0);
        return;
      }
      try {
        const globalVarsSnap = await getDoc(doc(db, "Global_Vars", "Main"));
        const { globalBaseTaxRate = 1.0 } = globalVarsSnap.data() || {};
        const taxDivisor = company.companyTicker === "XAU" ? 300 : 350;
        setTaxes((globalBaseTaxRate / taxDivisor) * (company.companyStockPrice * shares));
      } catch (err) {
        setTaxes(0);
      }
    };
    calculateTaxes();
  }, [shares, company, userData]);

  // Recipient autocomplete logic
  useEffect(() => {
    const term = recipient.trim().toLowerCase();
    if (!term) {
      setSearchResults([]);
      return;
    }
    const matches = allRecipients.filter((p) =>
      p.displayName.toLowerCase().startsWith(term)
    );
    setSearchResults(matches.slice(0, 5));
  }, [recipient, allRecipients]);

  const handleTransfer = async () => {
    if (!recipient.trim() || shares < 1) {
      addNotification("Recipient and quantity must be valid", "error");
      return;
    }
    setShowConfirm(false);
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://transactiontransfer-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ticker: company.companyTicker,
          quantity: shares,
          recipient: recipient.trim(),
        }),
      });
      const data = await res.json();
      if (data?.success) {
        addNotification(data.message, "success");
        if (refreshUserData) await refreshUserData();
      } else {
        addNotification(data?.message || "Transfer failed", "error");
      }
      onClose();
    } catch (err) {
      addNotification("Error executing transfer.", "error");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const unitLabel = company.companyCrypto ? "coins" : ["BND3", "BND7", "BND14"].includes(company.companyTicker) ? "bonds" : company.companyTicker === "XAU" ? "gold units" : "shares";

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
      backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(5px)",
      display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000,
      fontFamily: "'JetBrains Mono', monospace"
    }}>
      <div style={{
        width: "400px", background: "rgba(0, 30, 30, 0.9)", backdropFilter: "blur(10px)",
        border: "1px solid rgba(80, 227, 194, 0.2)", padding: "20px", position: "relative",
        boxShadow: "0 10px 40px rgba(0,0,0,0.7)"
      }}>
        <h2 style={{ color: "#fff", margin: "0 0 20px 0", fontSize: "1.2rem", borderBottom: "1px solid rgba(80, 227, 194, 0.2)", paddingBottom: "10px" }}>
           {company.companyName}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "10px" }}>
            <div style={{ color: "#888", fontSize: "0.55rem" }}>CURRENT HOLDINGS</div>
            <div style={{ color: "#eee", fontSize: "0.9rem" }}>{maxShares.toLocaleString()} <span style={{fontSize: '0.6rem'}}>{unitLabel}</span></div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "10px" }}>
            <div style={{ color: "#888", fontSize: "0.55rem" }}>EST UNIT VALUE</div>
            <div style={{ color: "#50e3c2", fontSize: "0.9rem" }}>${priceUSD.toFixed(2)}</div>
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <label style={{ color: "#888", fontSize: "0.65rem" }}>TRANSFER QUANTITY</label>
          </div>
          <input 
            type="number" value={shares} 
            onChange={(e) => setShares(Math.max(1, Math.min(maxShares, Number(e.target.value))))}
            style={{ width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
          />
          <input 
            type="range" min="1" max={maxShares || 1} value={shares}
            onChange={(e) => setShares(Number(e.target.value))}
            style={{ width: "100%", marginTop: "10px", accentColor: "#50e3c2" }}
          />
        </div>

        <div style={{ marginBottom: "20px", position: "relative" }}>
          <label style={{ color: "#888", fontSize: "0.65rem", display: "block", marginBottom: "10px" }}>RECIPIENT ID</label>
          <input 
            ref={recipientRef}
            type="text" placeholder="SEARCH PLAYER..."
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            autoComplete="off"
            style={{ width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
          />
          {searchResults.length > 0 && (
            <div ref={suggestionsRef} style={{
              position: "absolute", top: "100%", left: 0, width: "100%", background: "#0a1414", 
              border: "1px solid #50e3c2", zIndex: 20, marginTop: "2px", maxHeight: "150px", overflowY: "auto"
            }}>
              {searchResults.map((p) => (
                <div 
                  key={p.id}
                  onClick={() => { setRecipient(p.displayName); setSearchResults([]); }}
                  style={{ padding: "10px", color: "#50e3c2", cursor: "pointer", fontSize: "0.8rem", borderBottom: "1px solid rgba(80,227,194,0.1)" }}
                >
                  {p.displayName}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: "rgba(0,0,0,0.2)", padding: "15px", marginBottom: "20px", border: "1px solid rgba(255,255,255,0.05)" }}>
           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", fontSize: '0.7rem' }}>
             <span style={{ color: "#888" }}>TRANSFER VALUE:</span>
             <span style={{ color: "#eee" }}>{formatCurrency(priceUSD * shares)}</span>
           </div>
           <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
             <span style={{ color: "#888", fontSize: "0.6rem" }}>TAX COST (CASH):</span>
             <span style={{ color: "#ff7b72" }}>{formatCurrency(taxes)}</span>
           </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#888", cursor: "pointer", fontFamily: "inherit" }}>CANCEL</button>
          <button 
            onClick={() => setShowConfirm(true)}
            disabled={shares < 1 || maxShares === 0 || !recipient.trim() || loading}
            style={{
              flex: 2, padding: "12px", background: "#50e3c2",
              border: "none", color: "#000", fontWeight: "bold", cursor: "pointer",
              opacity: (shares < 1 || maxShares === 0 || !recipient.trim()) ? 0.5 : 1, fontFamily: "inherit"
            }}
          >
            TRANSFER
          </button>
        </div>

        {showConfirm && (
          <div style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            background: "rgba(0, 20, 20, 0.98)", backdropFilter: 'blur(5px)',
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", 
            padding: "20px", boxSizing: "border-box", border: "2px solid #50e3c2", textAlign: "center", zIndex: 10
          }}>
            <div style={{ color: "#50e3c2", fontSize: "0.6rem", marginBottom: "10px" }}>&gt; SECURE TRANSFER AUTH</div>
            <div style={{ color: "#fff", marginBottom: "20px", fontSize: "0.8rem" }}>
              Send <span style={{color:'#50e3c2'}}>{shares}</span> {unitLabel} to <br/> 
              <span style={{fontSize: '1rem', fontWeight: 'bold', color: '#ffd700'}}>{recipient}</span>?
            </div>
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", fontFamily: "inherit" }}>CANCEL</button>
              <button onClick={handleTransfer} style={{ flex: 1, padding: "10px", background: "#50e3c2", color: "#000", border: "none", fontWeight: "bold", fontFamily: "inherit" }}>CONFIRM</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default TransferSharesModal;